import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Setup axios interceptor to attach token
const setupAxiosAuth = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // First try localStorage token
    const savedToken = localStorage.getItem('access_token');
    if (savedToken) {
      setupAxiosAuth(savedToken);
    }
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      // Token expired or invalid - clear it
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setupAxiosAuth(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, 
        { email, password },
        { withCredentials: true }
      );
      const data = response.data;
      // Store tokens in localStorage as fallback for cookies
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        setupAxiosAuth(data.access_token);
      }
      setUser(data);
      return { success: true, user: data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Identifiants incorrects' };
    }
  };

  const register = async (email, password, name, role = 'merchant') => {
    const response = await axios.post(`${API_URL}/api/auth/register`,
      { email, password, name, role },
      { withCredentials: true }
    );
    const data = response.data;
    if (data.access_token) {
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      setupAxiosAuth(data.access_token);
    }
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (e) {}
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setupAxiosAuth(null);
    setUser(null);
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
